import React, { useState, useEffect } from 'react';
import { getPendingWithdrawals, approveWithdrawal, rejectWithdrawal } from '../../services/api';
import toast from 'react-hot-toast';
import { CheckCircleIcon, XCircleIcon, ArrowPathIcon } from '@heroicons/react/24/outline';

const WithdrawalApproval = () => {
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  const [showRejectModal, setShowRejectModal] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  useEffect(() => {
    fetchWithdrawals();
  }, []);

  const fetchWithdrawals = async () => {
    try {
      const response = await getPendingWithdrawals();
      if (response.data.success) {
        setWithdrawals(response.data.withdrawals);
      }
    } catch (error) {
      toast.error('Failed to fetch pending withdrawals');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (id) => {
    setProcessing(id);
    try {
      const response = await approveWithdrawal(id);
      if (response.data.success) {
        toast.success('Withdrawal approved and sent');
        fetchWithdrawals();
      }
    } catch (error) {
      toast.error('Failed to approve withdrawal');
    } finally {
      setProcessing(null);
    }
  };

  const handleReject = async () => {
    if (!showRejectModal) return;

    if (!rejectReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }

    try {
      const response = await rejectWithdrawal(showRejectModal, rejectReason);
      if (response.data.success) {
        toast.success('Withdrawal rejected');
        setShowRejectModal(null);
        setRejectReason('');
        fetchWithdrawals();
      }
    } catch (error) {
      toast.error('Failed to reject withdrawal');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-pulse text-gray-400">Loading withdrawal requests...</div>
      </div>
    );
  }

  return (
    <div className="dashboard-page fade-in">
      <div className="dashboard-head">
        <h1 className="dashboard-page-title">Withdrawal Approval</h1>
        <p className="dashboard-section-subtitle">Review and process user withdrawal requests</p>
      </div>

      {withdrawals.length === 0 ? (
        <section className="card dashboard-section">
          <div className="dashboard-section-body">
            <div className="styled-empty-state">
              <div className="empty-state-icon">
                <svg
                  className="h-8 w-8 flex-none text-emerald-400"
                  width="32"
                  height="32"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2.5"
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <p className="styled-empty-title">No Pending Withdrawals</p>
              <p className="styled-empty-subtitle">All withdrawal requests have been processed.</p>
            </div>
          </div>
        </section>
      ) : (
        <section className="card dashboard-section">
          <div className="dashboard-section-header">
            <h2 className="dashboard-section-title">Pending Requests</h2>
            <p className="dashboard-section-subtitle">Approve or reject withdrawal requests</p>
          </div>

          <div className="dashboard-section-body">
            <div className="market-table-wrap">
              <table className="table-dark w-full">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Amount</th>
                    <th>Destination Address</th>
                    <th>Requested</th>
                    <th>KYC Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {withdrawals.map((wd) => (
                    <tr key={wd.id}>
                      <td>
                        <div>
                          <p className="text-sm font-medium text-white">{wd.email || wd.phone}</p>
                          <p className="text-xs text-gray-500">ID: {wd.user_id}</p>
                        </div>
                      </td>

                      <td>
                        <span className="withdraw-approval-amount">
                          {parseFloat(wd.amount).toFixed(2)} USDT
                        </span>
                      </td>

                      <td>
                        <p className="withdraw-approval-address">
                          {wd.destination_address.slice(0, 12)}...{wd.destination_address.slice(-8)}
                        </p>
                      </td>

                      <td className="text-gray-400 text-sm">
                        {new Date(wd.requested_at).toLocaleString()}
                      </td>

                      <td>
                        <span className={wd.kyc_status === 'approved' ? 'badge badge-success' : 'badge badge-warning'}>
                          {wd.kyc_status}
                        </span>
                      </td>

                      <td>
                        <div className="withdraw-approval-actions">
                          <button
                            onClick={() => handleApprove(wd.id)}
                            disabled={processing === wd.id}
                            className="kyc-action-btn approve withdraw-approval-btn"
                            type="button"
                          >
                            {processing === wd.id ? (
                              <ArrowPathIcon className="w-4 h-4 animate-spin" />
                            ) : (
                              <CheckCircleIcon className="w-4 h-4" />
                            )}
                            Approve
                          </button>

                          <button
                            onClick={() => setShowRejectModal(wd.id)}
                            className="kyc-action-btn reject withdraw-approval-btn"
                            type="button"
                          >
                            <XCircleIcon className="w-4 h-4" />
                            Reject
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      )}

      {showRejectModal && (
        <div className="kyc-modal-overlay">
          <div className="kyc-modal-card">
            <h3 className="kyc-modal-title">Reject Withdrawal</h3>
            <p className="kyc-modal-subtitle">
              Are you sure you want to reject this withdrawal request?
            </p>

            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              className="auth-input kyc-modal-textarea"
              placeholder="Reason for rejection..."
            />

            <div className="kyc-modal-actions">
              <button
                onClick={handleReject}
                className="kyc-action-btn reject"
                type="button"
              >
                Confirm Reject
              </button>

              <button
                onClick={() => {
                  setShowRejectModal(null);
                  setRejectReason('');
                }}
                className="admin-action-btn"
                type="button"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WithdrawalApproval;